import { supabase } from '../../lib/supabase';
import { Platform } from 'react-native';
import { uploadAsync, FileSystemUploadType } from 'expo-file-system/legacy';

const BUCKET = 'profile-images';

/**
 * Converts a local URI to a Blob for web upload
 */
async function uriToBlob(uri: string): Promise<Blob> {
    const response = await fetch(uri);
    return response.blob();
}

export async function uploadProfileImage(uri: string, userId: string): Promise<string> {
    const fileExt = uri.split(".").pop()?.toLowerCase() || "jpg";
    const fileName = `${userId}_${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    const uploadUrl = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/${BUCKET}/${filePath}`;

    const { data: session } = await supabase.auth.getSession();
    const accessToken = session?.session?.access_token;

    if (!accessToken) {
        throw new Error('No user session—cannot upload.');
    }

    const contentType = `image/${fileExt}`;

    if (Platform.OS === 'web') {
        // Web: convert URI to blob and use fetch
        const blob = await uriToBlob(uri);
        const res = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': contentType,
                'x-upsert': 'true',
            },
            body: blob,
        });

        if (!res.ok) {
            throw new Error(`Upload failed with status ${res.status}`);
        }
    } else {
        // Native (iOS/Android): use expo-file-system uploadAsync
        const uploadResult = await uploadAsync(uploadUrl, uri, {
            httpMethod: "PUT",
            uploadType: FileSystemUploadType.BINARY_CONTENT,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": contentType,
                "x-upsert": "true",
            },
        });

        if (uploadResult.status !== 200) {
            throw new Error(`Upload failed with status ${uploadResult.status}`);
        }
    }

    // Construct public URL manually
    const publicUrl = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${filePath}`;
    return publicUrl;
}
/**
 * Deletes a previously uploaded image from the `profile-images` bucket.
 * Takes the `avatar_url` (which includes the bucket URL), extracts the path,
 * and removes the file.
 */
export async function deleteProfileImage(avatarUrl: string) {
    try {
        const pathAfterBucket = avatarUrl.split("/profile-images/")[1];
        if (!pathAfterBucket) {
            console.warn("Could not extract path from avatar URL");
            return;
        }

        const { error } = await supabase.storage
            .from("profile-images")
            .remove([pathAfterBucket]);

        if (error) {
            console.warn("Failed to delete old avatar:", error.message);
        } else {
            console.log("Avatar deleted successfully");
        }
    } catch (err) {
        console.warn("Error deleting profile image:", err);
    }
}